import {
  Combobox as BaseCombobox,
  type ComboboxPositionerProps,
  type ComboboxRootChangeEventDetails,
} from "@base-ui/react/combobox";
import classNames from "classnames";
import { ChevronDown as ChevronDownIcon } from "lucide-react";
import * as React from "react";

import { SubstringHighlight } from "@/shared/text/SubstringHighlight";

import styles from "./Combobox.module.css";

/** Control density: drives the trigger row's height, padding, and font size. */
export type ComboboxSize = "sm" | "md" | "lg";

const sizeClass: Record<ComboboxSize, string> = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
};

interface ComboboxPartContextValue {
  /** Id of the `<input>`, so the root's `<label>` can name it. */
  inputId: string;
  /**
   * The query the marks are allowed to claim — blank whenever the list on
   * screen isn't actually narrowed by it. See the root for why they differ.
   */
  highlightQuery: string;
  /** Size of the whole (unfiltered) corpus, for `MatchCount`. */
  itemCount: number;
  size: ComboboxSize;
  /** Erased to `unknown` — the parts are shared by every instantiation of the
   *  generic root, so the context itself can't be generic. */
  toString: (item: unknown) => string;
}

const ComboboxPartContext =
  React.createContext<ComboboxPartContextValue | null>(null);

function useComboboxPartContext(part: string): ComboboxPartContextValue {
  const context = React.useContext(ComboboxPartContext);
  if (context === null) {
    throw new Error(
      `<Combobox.${part}> must be rendered inside a <Combobox>. The parts read the input id, query, and size from it.`,
    );
  }
  return context;
}

function isPrimitive(value: unknown): value is string | number | boolean {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

/**
 * The text that stands for an item: what a bare `Item` renders, what the
 * default filter matches, and what the marks are checked against.
 *
 * Resolved in the order Base UI resolves the input's own text — the caller's
 * `itemToStringLabel`, then the conventional `label` and `value` fields, then
 * a serialization — so the rows can't name an item differently from the input
 * above them.
 */
function resolveItemLabel<T>(
  item: T,
  itemToStringLabel?: (item: T) => string,
): string {
  if (item === null || item === undefined) {
    return "";
  }

  if (itemToStringLabel) {
    return itemToStringLabel(item);
  }

  if (typeof item === "object") {
    // Only a primitive field can become input text. A `label` holding a
    // ReactNode is exactly the case `String` would turn into "[object Object]".
    if ("label" in item && isPrimitive(item.label)) {
      return String(item.label);
    }
    if ("value" in item && isPrimitive(item.value)) {
      return String(item.value);
    }
    // Nothing conventional to read. Serialize rather than render the
    // "[object Object]" that `String` would give.
    try {
      return JSON.stringify(item);
    } catch {
      return String(item);
    }
  }

  return String(item);
}

/**
 * Details accompanying an input edit — most usefully `reason`, which says what
 * caused it. Re-exported so a consumer can type a named `onInputValueChange`
 * handler without reaching past the foundation seam into `@base-ui/react`.
 */
export type ComboboxInputChangeEventDetails = ComboboxRootChangeEventDetails;

/**
 * Match test for one item against the query. The third argument is the root's
 * `itemToStringLabel`, which Base UI supplies so a rule can match against the
 * same text the input shows.
 */
export type ComboboxFilter<T> = (
  item: T,
  query: string,
  itemToString?: (item: T) => string,
) => boolean;

export interface ComboboxRootProps<T> {
  /**
   * Every selectable item, in the order they should be offered.
   */
  items: readonly T[];

  /** The committed item, or `null` when nothing is chosen. */
  value: T | null;

  /**
   * Fired with the newly committed item, or `null` when the user clears the
   * input.
   */
  onValueChange: (value: T | null) => void;

  /** Accessible name for the input, rendered as a visible `<label>` above it. */
  label?: React.ReactNode;

  /**
   * Control density of the trigger row. Popup density is unaffected.
   * @default "md"
   */
  size?: ComboboxSize;

  /**
   * Match test for one item against the query. `itemToString` is the root's
   * `itemToStringLabel`, so a rule can match the text the input shows.
   *
   * Pass `null` when the server already filtered `items`.
   *
   * `MatchText` marks literal substrings, so a rule matching anything else can
   * leave a row unmarked.
   */
  filter?: ComboboxFilter<T> | null;

  /**
   * Fired on every input edit. `eventDetails.reason` says what caused it;
   * server-backed consumers fetch from here and skip `"item-press"`, which is
   * Base UI writing the committed label back into the input.
   */
  onInputValueChange?: (
    inputValue: string,
    eventDetails: ComboboxInputChangeEventDetails,
  ) => void;

  /** Fired once the open/close animation has finished. */
  onOpenChangeComplete?: (open: boolean) => void;

  /**
   * How an item becomes the text shown in the input and matched by the default
   * filter.
   *
   * Provide this whenever the item type is not a string.
   */
  itemToStringLabel?: (item: T) => string;

  /**
   * Identity test between an item and the committed value. Needed when items
   * are objects that don't match referentially (e.g. refetched from a server).
   * @default Object.is
   */
  isItemEqualToValue?: (item: T, value: T) => boolean;

  /**
   * Whether the first match is highlighted as the user types, so Enter commits
   * it without arrowing to it first.
   * @default false
   */
  autoHighlight?: boolean;

  /** Whether the field ignores user interaction. */
  disabled?: boolean;

  /** Extra class(es) for the field wrapper — size the field here. */
  className?: string;

  children?: React.ReactNode;
}

/**
 * A single-select searchable dropdown
 *
 * @example
 * ```tsx
 * <Combobox items={metricNames} value={metric} onValueChange={setMetric} label="Metric">
 *   <Combobox.TriggerInput placeholder="Search metrics" />
 *   <Combobox.Content>
 *     <Combobox.Empty />
 *     <Combobox.List>
 *       {(name: string) => <Combobox.Item key={name} value={name} />}
 *     </Combobox.List>
 *     <Combobox.MatchCount noun="metrics" />
 *   </Combobox.Content>
 * </Combobox>
 * ```
 */
function ComboboxRoot<T>({
  items,
  value,
  onValueChange,
  label,
  size = "md",
  filter,
  onInputValueChange,
  onOpenChangeComplete,
  itemToStringLabel,
  isItemEqualToValue,
  autoHighlight,
  disabled = false,
  className,
  children,
}: ComboboxRootProps<T>) {
  const inputId = React.useId();
  const [query, setQuery] = React.useState("");

  const toString = React.useCallback(
    (item: T) => resolveItemLabel(item, itemToStringLabel),
    [itemToStringLabel],
  );

  // Base UI offers the whole list — rather than only what matches — while the
  // input still holds the committed value untouched. Blank the query to match,
  // so the marks never claim a filtering that isn't happening.
  const committedText = value === null ? "" : toString(value);
  const highlightQuery = query === committedText ? "" : query;

  const context = React.useMemo<ComboboxPartContextValue>(
    () => ({
      inputId,
      highlightQuery,
      itemCount: items.length,
      size,
      toString: toString as (item: unknown) => string,
    }),
    [inputId, highlightQuery, items.length, size, toString],
  );

  return (
    <BaseCombobox.Root<T>
      items={items}
      value={value}
      disabled={disabled}
      filter={filter}
      itemToStringLabel={itemToStringLabel}
      isItemEqualToValue={isItemEqualToValue}
      autoHighlight={autoHighlight}
      onOpenChangeComplete={onOpenChangeComplete}
      onValueChange={onValueChange}
      onInputValueChange={(nextInputValue, eventDetails) => {
        setQuery(nextInputValue);
        onInputValueChange?.(nextInputValue, eventDetails);
      }}
    >
      <ComboboxPartContext.Provider value={context}>
        <div className={classNames(styles.field, className)}>
          {/* A native <label>, not Combobox.Label: that part labels the trigger
              button, and the input is the control being named here. */}
          {label !== undefined && (
            <label className={styles.label} htmlFor={inputId}>
              {label}
            </label>
          )}
          {children}
        </div>
      </ComboboxPartContext.Provider>
    </BaseCombobox.Root>
  );
}

export interface ComboboxTriggerInputProps {
  /** Text shown in the input while nothing is committed. */
  placeholder?: string;
  className?: string;
  /** Accessible name for the input when the field has no visible `label`. */
  "aria-label"?: string;
  /** Id of an element that names the input, such as its section's heading. */
  "aria-labelledby"?: string;
}

/**
 * The text input and the chevron that opens the popup, as one control.
 *
 * Forwards its ref to the input, not the row around it.
 */
const TriggerInput = React.forwardRef<
  HTMLInputElement,
  ComboboxTriggerInputProps
>(function TriggerInput(
  {
    placeholder,
    className,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
  },
  ref,
) {
  const { inputId, size } = useComboboxPartContext("TriggerInput");

  return (
    <div className={classNames(styles.inputRow, sizeClass[size], className)}>
      <BaseCombobox.Input
        ref={ref}
        id={inputId}
        className={styles.input}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
      />
      <BaseCombobox.Trigger
        className={styles.trigger}
        aria-label="Show options"
      >
        <BaseCombobox.Icon className={styles.icon}>
          <ChevronDownIcon aria-hidden />
        </BaseCombobox.Icon>
      </BaseCombobox.Trigger>
    </div>
  );
});

export interface ComboboxContentProps {
  children?: React.ReactNode;
  /**
   * Extra class(es) for the popup surface.
   *
   * Set `--combobox-max-height` in one to cap how tall the popup grows (default `20rem`).
   * It stays within the room between the field and the viewport edge either way.
   */
  className?: string;
  /** @default 4 */
  sideOffset?: number;
  /** @default 8 */
  collisionPadding?: ComboboxPositionerProps["collisionPadding"];
  side?: ComboboxPositionerProps["side"];
  align?: ComboboxPositionerProps["align"];
  /** Whether results are still loading, announced as `aria-busy`. */
  busy?: boolean;
}

/**
 * The floating popup. Portal, positioning, and z-index are fused in here
 * because they're system decisions, not per-call-site ones.
 */
function Content({
  children,
  className,
  sideOffset = 4,
  collisionPadding = 8,
  side,
  align,
  busy,
}: ComboboxContentProps) {
  return (
    <BaseCombobox.Portal>
      <BaseCombobox.Positioner
        className={styles.positioner}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        side={side}
        align={align}
      >
        <BaseCombobox.Popup
          className={classNames(styles.popup, className)}
          aria-busy={busy}
        >
          {children}
        </BaseCombobox.Popup>
      </BaseCombobox.Positioner>
    </BaseCombobox.Portal>
  );
}

export interface ComboboxListProps<T> {
  /**
   * Renders one row. Base UI types its items as `any`, so the annotation on
   * this parameter is what re-imposes the item type on the rows.
   */
  children: (item: T) => React.ReactNode;
  className?: string;
}

/** The scrollable list of matching items. */
function List<T>({ children, className }: ComboboxListProps<T>) {
  return (
    <BaseCombobox.List className={classNames(styles.list, className)}>
      {children}
    </BaseCombobox.List>
  );
}

export interface ComboboxItemProps<T> {
  value: T;
  disabled?: boolean;
  className?: string;
  /** Defaults to the item's label with the query marked. */
  children?: React.ReactNode;
}

/** One selectable row. */
function Item<T>({
  value,
  disabled,
  className,
  children,
}: ComboboxItemProps<T>) {
  const { toString } = useComboboxPartContext("Item");

  return (
    <BaseCombobox.Item
      value={value}
      disabled={disabled}
      className={classNames(styles.item, className)}
    >
      {children ?? <MatchText text={toString(value)} />}
    </BaseCombobox.Item>
  );
}

export interface ComboboxMatchTextProps {
  text: string;
}

/**
 * Text with the current query marked. Reads the query from the root rather
 * than taking it as a prop, so a custom row can't mark against a query the
 * list was never filtered by.
 */
function MatchText({ text }: ComboboxMatchTextProps) {
  const { highlightQuery } = useComboboxPartContext("MatchText");

  return (
    <SubstringHighlight
      text={text}
      query={highlightQuery}
      markClassName={styles.mark}
    />
  );
}

export interface ComboboxEmptyProps {
  /** @default "No matches" */
  children?: React.ReactNode;
  className?: string;
}

/** Shown in place of the list when the query matches nothing. */
function Empty({ children = "No matches", className }: ComboboxEmptyProps) {
  return (
    <BaseCombobox.Empty className={classNames(styles.empty, className)}>
      {children}
    </BaseCombobox.Empty>
  );
}

export interface ComboboxStatusProps {
  children?: React.ReactNode;
  className?: string;
}

/**
 * A politely announced row for the state of an asynchronous list ("Searching…",
 * an error). Render `null` children rather than unmounting this: Base UI needs
 * the element to stay put for screen readers to announce the change.
 */
function Status({ children, className }: ComboboxStatusProps) {
  return (
    <BaseCombobox.Status className={classNames(styles.status, className)}>
      {children}
    </BaseCombobox.Status>
  );
}

export interface ComboboxMatchCountProps {
  /** Plural noun for the items, e.g. `"metrics"`. */
  noun: string;
  /**
   * Size of the whole corpus. Defaults to the length of `items`; pass it when
   * the server holds more than the page in `items` ("50 of 10,000 metrics").
   */
  total?: number;
  className?: string;
}

/**
 * How much of the list the query is hiding. Reads the filtered items from Base
 * UI rather than re-running the filter, so the count can never disagree with
 * the rows above it.
 *
 * Renders nothing while no rows are shown — an empty list, or a server search
 * still in flight.
 */
function MatchCount({ noun, total, className }: ComboboxMatchCountProps) {
  const { itemCount } = useComboboxPartContext("MatchCount");
  const shown = BaseCombobox.useFilteredItems<unknown>().length;

  // With no rows there is nothing to count, `Empty` is already saying so, and
  // the footer's top border would draw a rule under an empty popup.
  if (shown === 0) {
    return null;
  }

  return (
    <div
      className={classNames(styles.count, className)}
      data-testid="combobox-match-count"
    >
      {shown} of {total ?? itemCount} {noun}
    </div>
  );
}

/**
 * `Object.assign` rather than a plain object literal: it keeps the root's
 * generic call signature, so `<Combobox items={metrics}>` still infers the item
 * type while `<Combobox.Item>` hangs off the same name.
 */
export const Combobox = Object.assign(ComboboxRoot, {
  TriggerInput,
  Content,
  List,
  Item,
  MatchText,
  Empty,
  Status,
  MatchCount,
  useFilter: BaseCombobox.useFilter,
});
